/**
 * Execution Status Utilities
 *
 * Centralized utilities for normalizing and checking execution status values.
 * Handles all status format variations: 'completed', 'succeeded', 'SUCCEEDED', 'success', etc.
 */

export type ExecutionStatus =
  | 'completed'
  | 'partial_success'
  | 'succeeded'
  | 'success'
  | 'failed'
  | 'failure'
  | 'error'
  | 'running'
  | 'in_progress'
  | 'pending'
  | 'queued'
  | 'cancelled'
  | 'unknown';

/**
 * Normalize status value to lowercase for consistent comparison
 * Handles null, undefined, and various case formats
 */
export function normalizeStatus(status: string | null | undefined): string {
  if (!status) return '';
  return status.toLowerCase().trim();
}

/**
 * Check if status indicates a successful completion
 * Handles: 'completed', 'succeeded', 'SUCCEEDED', 'success'
 */
export function isCompletedStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status);
  return normalized === 'completed' || normalized === 'succeeded' || normalized === 'success';
}

/**
 * Check if status indicates a partial success
 * Handles: 'partial_success'
 */
export function isPartialSuccessStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status);
  return normalized === 'partial_success';
}

/**
 * Check if status indicates a failure
 * Handles: 'failed', 'failure', 'error'
 */
export function isFailedStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status);
  return normalized === 'failed' || normalized === 'failure' || normalized === 'error';
}

/**
 * Check if status indicates a cancellation
 * Handles: 'cancelled'
 */
export function isCancelledStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status);
  return normalized === 'cancelled';
}

/**
 * Check if status indicates the task is still running
 * Handles: 'running', 'in_progress', 'pending', 'queued'
 */
export function isRunningStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status);
  return (
    normalized === 'running' ||
    normalized === 'in_progress' ||
    normalized === 'pending' ||
    normalized === 'queued'
  );
}

/**
 * Check if status indicates the task is finished (completed or failed)
 */
export function isFinishedStatus(status: string | null | undefined): boolean {
  return (
    isCompletedStatus(status) ||
    isPartialSuccessStatus(status) ||
    isFailedStatus(status) ||
    isCancelledStatus(status)
  );
}

/**
 * Get a normalized status value for display
 * Returns one of: 'completed', 'failed', 'running', 'unknown'
 */
export function getNormalizedStatus(status: string | null | undefined): ExecutionStatus {
  if (isCompletedStatus(status)) return 'completed';
  if (isPartialSuccessStatus(status)) return 'partial_success';
  if (isFailedStatus(status)) return 'failed';
  if (isCancelledStatus(status)) return 'cancelled';
  if (isRunningStatus(status)) return 'running';
  return 'unknown';
}

/**
 * Check if status should be considered "active" (not finished)
 */
export function isActiveStatus(status: string | null | undefined): boolean {
  return !isFinishedStatus(status);
}
